import type Immutable from 'immutable';
import { createAction } from '@reduxjs/toolkit';

import type {
    AssetContainerModel,
    AssetContainerTypes,
    AssetTypes,
    FavoritedAsset,
    GenerativeMediaModels,
    MediaFolder,
    SlideBackgroundModel,
    SlideBackgroundModelTypeEnum,
    SortEnum,
    SuggestedMediaEntryResponse,
    TaskResultStatusEnum,
    ToggleFavoritedAsset,
    TransitionedAssetError,
} from 'js/src/client';
import type { UploadedFile } from 'js/src/components/sketch/FileUpload';
import type { MediaFilter } from 'js/src/data/media/cookie-operations';
import type {
    AI_MEDIA_SUB_TAB_OPTIONS,
    AudioDurationRange,
    FILTER_BY_LEVELS,
    MEDIA_LICENSE_OPTIONS,
    MEDIA_SOURCE_OPTIONS,
    MediaFolderWithContents,
    ModifiedAssetGenerationResponse,
    SearchContextValueType,
    SearchResponse,
} from 'js/src/data/media/media-types';
import { MEDIA_ACTIONS } from 'js/src/data/media/media-types';
import type { ImmutableMap } from 'js/src/libs/consts';

/*

NOTE!

If you are creating an action to be routed to a specific section of the media library,
you MUST include search_context and folderId as properties of your action.

See js/src/data/media/index.ts for more information, where there is custom action-routing logic.
*/

export const setFilter = createAction<{
    search_context: SearchContextValueType;
    filter: ImmutableMap<Partial<Record<MEDIA_SOURCE_OPTIONS, boolean>>>;
    folderId?: number;
}>(MEDIA_ACTIONS.SET_FILTER);

export const setSort = createAction<{
    search_context: SearchContextValueType;
    sort_method: SortEnum;
    folderId?: number;
}>(MEDIA_ACTIONS.SET_SORT);

export const changeMediaSearchTerm = createAction<{
    search_context: SearchContextValueType;
    search_term: string;
    folderId?: number;
}>(MEDIA_ACTIONS.CHANGE_MEDIA_SEARCH_TERM);

export const changeCorrectedMediaSearchTerm = createAction<{
    search_context: SearchContextValueType;
    search_term: string;
    search_term_indices: Array<number>;
}>(MEDIA_ACTIONS.CHANGE_CORRECTED_MEDIA_SEARCH_TERM);

export const changeSuggestedMediaSearchTerm = createAction<{
    search_context: SearchContextValueType;
    query: string;
    highlighted_query: string;
}>(MEDIA_ACTIONS.CHANGE_SUGGESTED_MEDIA_SEARCH_TERM);

export const changeRelatedMediaSearchTerms = createAction<{
    search_context: SearchContextValueType;
    popular_queries: string[];
}>(MEDIA_ACTIONS.CHANGE_RELATED_MEDIA_SEARCH_TERMS);

export const storeFrequentlyUsedTags = createAction<{
    folderId: number | null;
    search_context: SearchContextValueType;
    frequently_used_tags: Immutable.List<string>;
}>(MEDIA_ACTIONS.STORE_FREQUENTLY_USED_TAGS);

export const changeActiveSearchContext = createAction<{
    search_context: SearchContextValueType;
}>(MEDIA_ACTIONS.CHANGE_ACTIVE_SEARCH_CONTEXT);

export const updateMediaTags = createAction<{
    mediaORMId: any;
    mediaType: AssetTypes;
    mediaTags: Immutable.List<string>;
    folderId: number | null;
    sendToAllMediaReducers: boolean;
}>(MEDIA_ACTIONS.UPDATE_MEDIA_TAGS);

export const updateMediaAttribution = createAction<{
    mediaORMId: any;
    mediaType: AssetTypes;
    mediaAttribution: string;
    folderId: number | null;
    sendToAllMediaReducers: boolean;
}>(MEDIA_ACTIONS.UPDATE_MEDIA_ATTRIBUTION);

export const receiveMediaResponse = createAction<{
    response: SearchResponse;
    search_context: SearchContextValueType;
    folderId: number | null;
    page: number;
    pos: string | null;
    append_results: boolean;
    num_existing_results: number;
}>(MEDIA_ACTIONS.RECEIVE_MEDIA_RESPONSE);

export const receiveDamConnectionError = createAction<{
    search_context: SearchContextValueType;
}>(MEDIA_ACTIONS.RECEIVE_DAM_CONNECTION_ERROR);

export const receiveMediaError = createAction<{
    search_context: SearchContextValueType;
    folderId: number | null;
}>(MEDIA_ACTIONS.RECEIVE_MEDIA_ERROR);

export const startMediaSearch = createAction<{
    search_term: string;
    page: number;
    search_context: SearchContextValueType;
    filter: MediaFilter[];
    append_results: boolean;
    folderId: number | null;
}>(MEDIA_ACTIONS.START_MEDIA_SEARCH);

export const toggleMediaSourceFilter = createAction<{
    search_context: SearchContextValueType;
    filter_key: any;
    filter_value: boolean;
    folderId?: number;
}>(MEDIA_ACTIONS.TOGGLE_MEDIA_SOURCE_FILTER);

export const setSelectedMediaTypeFilter = createAction<{
    search_context: SearchContextValueType;
    selected_type: any;
    folderId?: number;
}>(MEDIA_ACTIONS.SET_SELECTED_MEDIA_TYPE_FILTER);

export const setAudioDurationFilter = createAction<{
    search_context: SearchContextValueType;
    duration_range: AudioDurationRange;
    folderId?: number;
}>(MEDIA_ACTIONS.SET_AUDIO_DURATION_FILTER);

export const setSelectedMediaLicenseFilter = createAction<{
    search_context: SearchContextValueType;
    selected_license_type: MEDIA_LICENSE_OPTIONS;
    folderId?: number;
}>(MEDIA_ACTIONS.TOGGLE_MEDIA_LICENSE_FILTER);

export const toggleFilterByUserLevel = createAction<{
    search_context: SearchContextValueType;
    filter_by_user_level: FILTER_BY_LEVELS;
}>(MEDIA_ACTIONS.TOGGLE_FILTER_BY_USER_LEVEL);

export const setIsFetchingRecommendedSearchTerms = createAction<boolean>(
    MEDIA_ACTIONS.SET_IS_FETCHING_RECOMMENDED_SEARCH_TERMS,
);

export const setRecommendedSearchTerms = createAction<{
    terms: Immutable.List<string>;
    systemTriggered: boolean;
    parentActionGroup: string | undefined;
}>(MEDIA_ACTIONS.SET_RECOMMENDED_SEARCH_TERMS);

export const setCurrentMediaQueryScene = createAction<{
    sceneId: string | null;
}>(MEDIA_ACTIONS.SET_CURRENT_MEDIA_QUERY_SCENE);

export const setIsMediaQuerySearchLoading = createAction<{
    isLoading: boolean;
}>(MEDIA_ACTIONS.SET_IS_MEDIA_QUERY_SEARCH_LOADING);

export const setMediaQuerySearchResults = createAction<{
    searchResults: SuggestedMediaEntryResponse[];
}>(MEDIA_ACTIONS.SET_MEDIA_QUERY_SEARCH_RESULTS);

export const showMediaQuerySuggestionError = createAction<{
    show: boolean;
}>(MEDIA_ACTIONS.SHOW_MEDIA_QUERY_SUGGESTION_ERROR);

export const setIsSimilarSearchContext = createAction<boolean>(
    MEDIA_ACTIONS.SET_IS_SIMILAR_SEARCH_CONTEXT,
);
export const setIsSimilarSearchLoading = createAction<boolean>(
    MEDIA_ACTIONS.SET_IS_SIMILAR_SEARCH_LOADING,
);
export const setSimilarSearchResults = createAction<{
    results: Immutable.List<ImmutableMap<AssetContainerModel>>;
}>(MEDIA_ACTIONS.SET_SIMILAR_SEARCH_RESULTS);

export const setSimilarSearchOriginalAsset = createAction<{
    originalAsset: ImmutableMap<AssetContainerModel> | null;
}>(MEDIA_ACTIONS.SET_SIMILAR_SEARCH_ORIGINAL_ASSET);

export const setAllFavorites = createAction<{
    allFavoriteMedia: FavoritedAsset[];
}>(MEDIA_ACTIONS.SET_ALL_FAVORITES);

export const toggleFavorite = createAction<ToggleFavoritedAsset>(
    MEDIA_ACTIONS.TOGGLE_FAVORITE_MEDIA,
);

export const setCurrentPlayingAudioUrl = createAction<{
    audioUrl: string | null;
}>(MEDIA_ACTIONS.SET_CURRENT_PLAYING_AUDIO_URL);

export const setCurrentPlayingAudioSeekPercent = createAction<{
    seekPercent: number;
}>(MEDIA_ACTIONS.SET_CURRENT_PLAYING_AUDIO_SEEK_PERCENT);

export const setCurrentPlayingAudioScrubberSeekPercent = createAction<{
    scrubberSeekPercent: number;
}>(MEDIA_ACTIONS.SET_CURRENT_PLAYING_AUDIO_SCRUBBER_SEEK_PERCENT);

export const setCurrentPlayingAudioDuration = createAction<{
    duration: number;
}>(MEDIA_ACTIONS.SET_CURRENT_PLAYING_AUDIO_DURATION);

export const setCurrentAudioPlayingStatus = createAction<{
    isPlaying: boolean;
}>(MEDIA_ACTIONS.SET_CURRENT_AUDIO_PLAYING_STATUS);

export const storeAllFolders = createAction<{
    folders: Immutable.List<ImmutableMap<MediaFolderWithContents>>;
}>(MEDIA_ACTIONS.STORE_ALL_FOLDERS);

export const addNewFolder = createAction<{
    folder: ImmutableMap<MediaFolder>;
}>(MEDIA_ACTIONS.ADD_NEW_FOLDER);

export const editFolderName = createAction<{
    folder: ImmutableMap<MediaFolder>;
}>(MEDIA_ACTIONS.EDIT_FOLDER_NAME);

export const deleteFolder = createAction<{
    folderId: number;
}>(MEDIA_ACTIONS.DELETE_FOLDER);

export const setActiveFolder = createAction<{
    folderId: number | null;
    searchContext: SearchContextValueType;
}>(MEDIA_ACTIONS.SET_ACTIVE_FOLDER);

export const addRecentlyUploadedAssetToAFolder = createAction<{
    folderId: number;
    mediaAsset: AssetContainerModel;
}>(MEDIA_ACTIONS.ADD_RECENTLY_UPLOADED_ASSET_TO_A_FOLDER);

export const moveAssetToFolder = createAction<{
    targetFolderId: number;
    asset: ImmutableMap<AssetContainerModel>;
    search_context: SearchContextValueType | null;
    sourceFolderId: number | null;
}>(MEDIA_ACTIONS.MOVE_ASSET_TO_FOLDER);

export const addMediaToUploads = createAction<{
    mediaAsset: AssetContainerModel;
    search_context: SearchContextValueType;
}>(MEDIA_ACTIONS.ADD_MEDIA_TO_UPLOADS);

export const resetFolderSearchState = createAction<{
    folder_id: number;
}>(MEDIA_ACTIONS.RESET_FOLDER_SEARCH_STATE);

export const updateFolderThumbnail = createAction<{
    srcFolderId: number | null;
    srcThumbnailUrl: string | null;
    targetFolderId: number;
    targetThumbnailUrl: string | null;
}>(MEDIA_ACTIONS.UPDATE_FOLDER_THUMBNAIL);

export const deleteAudioSuccess = createAction<{
    id: number;
    search_context: SearchContextValueType;
}>(MEDIA_ACTIONS.DELETE_AUDIO_SUCCESS);

export const setCurrentMediaGenerations = createAction<Array<ModifiedAssetGenerationResponse>>(
    MEDIA_ACTIONS.SET_CURRENT_MEDIA_GENERATIONS,
);
export const addPreviousMediaGenerations = createAction<Array<ModifiedAssetGenerationResponse>>(
    MEDIA_ACTIONS.ADD_PREVIOUS_MEDIA_GENERATIONS,
);
export const setPreviousMediaGenerations = createAction<Array<ModifiedAssetGenerationResponse>>(
    MEDIA_ACTIONS.SET_PREVIOUS_MEDIA_GENERATIONS,
);
export const setSavingAIMedia = createAction<{ generationUuid: string; isSaving: boolean }>(
    MEDIA_ACTIONS.SET_SAVING_AI_MEDIA,
);
export const setMediaErrors = createAction<{
    errors: TransitionedAssetError[];
}>(MEDIA_ACTIONS.SET_MEDIA_ERRORS);

export const clearMediaErrors = createAction(MEDIA_ACTIONS.CLEAR_MEDIA_ERRORS);

export const setAISimilarGenerations = createAction<{
    results: ModifiedAssetGenerationResponse[];
    append: boolean;
}>(MEDIA_ACTIONS.SET_AI_SIMILAR_GENERATIONS);

export const setAISimilarGenerationsLoading = createAction<boolean>(
    MEDIA_ACTIONS.SET_IS_AI_SIMILAR_GENERATIONS_LOADING,
);

export const deleteMediaResult = createAction<{
    mediaUuid: string;
    assetType: AssetContainerTypes;
    slideBackgroundType: SlideBackgroundModelTypeEnum;
    search_context: SearchContextValueType;
}>(MEDIA_ACTIONS.DELETE_MEDIA_RESULT);

export const setAIMediaModels = createAction<GenerativeMediaModels[]>(
    MEDIA_ACTIONS.SET_AI_MEDIA_MODELS,
);

export const setVideoGenerationTask = createAction<{
    taskId: string;
    status: TaskResultStatusEnum;
    result?: SlideBackgroundModel;
}>(MEDIA_ACTIONS.SET_VIDEO_GENERATION_TASK);

export const clearCompletedVideoGenerationTasks = createAction(
    MEDIA_ACTIONS.CLEAR_COMPLETED_VIDEO_GENERATION_TASKS,
);

export const setChartGenerationAsset = createAction<AssetContainerModel | null>(
    MEDIA_ACTIONS.SET_CHART_GENERATION_ASSET,
);

export const setIsInitializingVideoGeneration = createAction<boolean>(
    MEDIA_ACTIONS.SET_IS_INITIALIZING_VIDEO_GENERATION,
);

export const setIsInitializingImageGeneration = createAction<boolean>(
    MEDIA_ACTIONS.SET_IS_INITIALIZING_IMAGE_GENERATION,
);

export const setSelectedReferenceImage = createAction<UploadedFile | null>(
    MEDIA_ACTIONS.SET_SELECTED_REFERENCE_IMAGE,
);

export const setActiveAIMediaSubTab = createAction<AI_MEDIA_SUB_TAB_OPTIONS>(
    MEDIA_ACTIONS.SET_ACTIVE_AI_MEDIA_SUB_TAB,
);

export const setCarouselMediaResults = createAction<{
    search_context: SearchContextValueType;
    categoryId: string;
    results: AssetContainerModel[];
}>(MEDIA_ACTIONS.SET_CAROUSEL_MEDIA_RESULTS);

export const setUserSelectedGenMediaAspectRatio = createAction<string | null>(
    MEDIA_ACTIONS.SET_USER_SELECTED_GEN_MEDIA_ASPECT_RATIO,
);
